import { z } from 'zod';

/**
 * Contracts for the admin scan endpoints (admin/scans…). Dates are ISO UTC strings.
 * Enum-like strings (status, engine, confidence) stay plain strings so a new value
 * shows up instead of breaking the page.
 *
 * @typedef {{ id: number, code: string, category: string, name: string, unit: string }} FuelOptionSummary
 * @typedef {{ engine: string|null, confidence: string, unitPrice: number|null, volume: number|null,
 *   totalAmount: number|null, fuelText: string|null, fuelOption: FuelOptionSummary|null, receiptAt: string|null,
 *   stationText: string|null, checks: Record<string, unknown> }} Ocr
 * @typedef {{ id: number, placeId: string|null, status: string, receiptUrl: string|null, fuelOption: FuelOptionSummary|null,
 *   unitPrice: number|null, volume: number|null, totalAmount: number|null, receiptAt: string|null, receiptNumber: string|null,
 *   ocr: Ocr, userEdited: boolean, submittedAt: string|null, reviewedAt: string|null, rejectReason: string|null,
 *   priceApplied: boolean|null, createdAt: string|null, user: { userId: string|null, name: string|null, email: string|null },
 *   reviewedBy: { userId: string|null, name: string|null }|null,
 *   station: { name: string|null, address: string|null, city: string|null, postalCode: string|null, googleStatus: string|null }|null,
 *   stationMatch: boolean|null,
 *   currentPrice: { latestPrice: number|null, isAvailable: boolean, source: string|null, sourceUrl: string|null, updatedAt: string|null }|null }} Scan
 * @typedef {Scan & { ocrText: string|null, pumpNumber: number|null,
 *   mathCheck: { expectedUnitPrice: number, expectedTotal: number, matches: boolean }|null, receiptAgeDays: number|null,
 *   queue: { total: number, position: number|null, prevId: number|null, nextId: number|null, firstPendingId: number|null }|null,
 *   userStats: { draft: number, pending: number, approved: number, rejected: number, submitted: number, scansMade: number|null }|null }} ScanDetail
 * @typedef {{ draft: number, pending: number, approved: number, rejected: number, all: number }} StatusCounts
 */

const str = z.string().nullable().optional().transform((v) => v ?? null);
// Decimal columns can arrive as strings; anything unparsable becomes null
const num = z
  .union([z.number(), z.string()])
  .nullable()
  .optional()
  .transform((v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });
const bool = z.union([z.boolean(), z.number()]).nullable().optional().transform((v) => (v === null || v === undefined ? null : Boolean(v)));
const optionalObject = (shape) => z.object(shape).nullable().optional().transform((v) => v ?? null);

const fuelOption = optionalObject({ id: z.number().int(), code: str, category: str, name: str, unit: str });

const ocrSchema = z
  .object({
    engine: str,
    confidence: z.string().nullable().optional().transform((v) => v ?? 'low'),
    unitPrice: num,
    volume: num,
    totalAmount: num,
    fuelText: str,
    fuelOption,
    receiptAt: str,
    stationText: str,
    checks: z.record(z.string(), z.unknown()).nullable().optional().transform((v) => v ?? {}),
  })
  .nullable()
  .optional()
  .transform(
    (v) =>
      v ?? { engine: null, confidence: 'low', unitPrice: null, volume: null, totalAmount: null, fuelText: null, fuelOption: null, receiptAt: null, stationText: null, checks: {} },
  );

export const scanSchema = z.object({
  id: z.number().int().positive(),
  placeId: str,
  status: z.string(),
  receiptUrl: str,
  fuelOption,
  unitPrice: num,
  volume: num,
  totalAmount: num,
  receiptAt: str,
  receiptNumber: str,
  ocr: ocrSchema,
  userEdited: z.union([z.boolean(), z.number()]).nullable().optional().transform((v) => Boolean(v)),
  submittedAt: str,
  reviewedAt: str,
  rejectReason: str,
  priceApplied: bool,
  createdAt: str,
  user: z
    .object({ userId: str, name: str, email: str })
    .nullable()
    .optional()
    .transform((v) => v ?? { userId: null, name: null, email: null }),
  reviewedBy: optionalObject({ userId: str, name: str }),
  station: optionalObject({ name: str, address: str, city: str, postalCode: str, googleStatus: str }),
  stationMatch: bool,
  currentPrice: optionalObject({
    latestPrice: num,
    isAvailable: z.union([z.boolean(), z.number()]).nullable().optional().transform((v) => v !== false && v !== 0),
    source: str,
    sourceUrl: str,
    updatedAt: str,
  }),
});

const count = z.number().int().nonnegative().nullable().optional().transform((v) => v ?? 0);
const id = z.number().int().positive().nullable().optional().transform((v) => v ?? null);

export const scanDetailSchema = scanSchema.extend({
  ocrText: str,
  pumpNumber: num,
  mathCheck: optionalObject({ expectedUnitPrice: num, expectedTotal: num, matches: z.boolean() }),
  receiptAgeDays: num,
  queue: optionalObject({ total: count, position: id, prevId: id, nextId: id, firstPendingId: id }),
  userStats: optionalObject({ draft: count, pending: count, approved: count, rejected: count, submitted: count, scansMade: num }),
});

export const scanListSchema = z.object({
  scans: z.array(z.unknown()),
  pagination: z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean(),
  }),
  statusCounts: z
    .object({ draft: count, pending: count, approved: count, rejected: count, all: count })
    .partial()
    .nullable()
    .optional()
    .transform((c) => ({ draft: c?.draft ?? 0, pending: c?.pending ?? 0, approved: c?.approved ?? 0, rejected: c?.rejected ?? 0, all: c?.all ?? 0 })),
});
